import React from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { BulkSelfServeFormProps, BulkSelfServeFormValues } from '../types';
import { Formik, FormikHelpers as FormikActions, Field } from 'formik';
import * as yup from 'yup';
import MultiConfirmationFormGroup from '../subscription_form/multi_confirmation_form_group';
import { PrimaryButton } from '@magoosh/lib/button';
import style from './style.module.scss';
import cx from 'classnames';
import { calculateSum, calculateSubtotal } from '../utils';
import { formatPrice } from 'utilities/money';
import { paths } from 'config/path_helpers';

const validationSchema = yup.object({
  authenticityToken: yup.string().required(),
  orderItems: yup.array(
    yup.object({
      purchasableCount: yup.number().label('Accounts').min(0)
    })
  )
});

export const PurchaseOptions: React.FC<BulkSelfServeFormProps> = (props) => {
  const initialValues: BulkSelfServeFormValues = {
    authenticityToken: props.authenticityToken,
    orderItems:
      props.subscriptionPlans &&
      props.subscriptionPlans.map((plan) => {
        return {
          purchasable: plan,
          purchasableCount: plan.initialAmount ? plan.initialAmount : 0,
          priceCentsPerItem: plan.priceCents,
          slug: plan.slug
        };
      })
  };

  // On submission, just navigate to the purchase page
  // with the chosen plan counts given as query params
  const handleOnSubmit = (
    values: BulkSelfServeFormValues,
    { setSubmitting }: FormikActions<BulkSelfServeFormValues>
  ): void => {
    setSubmitting(true);
    window.location.assign(paths.api.classCheckout(values.orderItems, props.nonprofit));
  };

  return (
    <div>
      <div className="text-center u-margin-B-xl">
        <h1>How many students do you have?</h1>
        <p>
          Please contact the Magoosh team at <a href="mailto:schools@magoosh.com">schools@magoosh.com</a> if
          you have 100 or more students! We can help you find the price that fits your budget!
        </p>
        {props.nonprofit && (
          <p className="text-secondary">
            This is a special rate for verified non-profit partners. <br />
            To ensure we can continue offering this program for students with the greatest need your order
            will be cancelled if we’re unable to verify eligibility.
          </p>
        )}
      </div>

      <Row>
        <Col xs={12} md={8} className="pricing-column pricing-column-primary">
          <div className="pricing-column-title">
            <h3>Magoosh Custom Plans</h3>
          </div>
          {props.subscriptionPlans.map((plan, index) => {
            return (
              <Row key={index} className={cx(style.bulkPricingColumn, 'pricing-column-header')}>
                <Col md={9}>
                  <h4>
                    <strong>{plan.name} - </strong> {plan.description}
                  </h4>
                </Col>
                <Col md={3}>
                  <h4>
                    <strong>{formatPrice(plan.priceCents, plan.currency)}</strong> each
                  </h4>
                </Col>
              </Row>
            );
          })}
          <Row className={cx(style.bulkPricingColumn, 'pricing-column-body')}>
            <p>
              <strong>Plans include:</strong>
            </p>
            <Col md={6} className={style.featuresCol}>
              <ul className="pricing-column-features">
                <li>
                  <i className="fa fa-check-circle"></i>
                  <span>
                    {' '}
                    <strong>12</strong> months of access
                  </span>
                </li>
                <li>
                  <i className="fa fa-check-circle"></i>
                  <span>
                    {' '}
                    <strong>Free</strong> instructor accounts
                  </span>
                </li>
              </ul>
            </Col>
            <Col md={6} className={style.featuresCol}>
              <ul className="pricing-column-features">
                <li>
                  <i className="fa fa-check-circle"></i> <span>Student progress tracking</span>
                </li>
                <li>
                  <i className="fa fa-check-circle"></i>
                  <span>
                    {' '}
                    Over <strong>2000</strong> practice questions
                  </span>
                </li>
              </ul>
            </Col>
          </Row>
          <Row className={cx(style.bulkPricingColumn, 'pricing-column-body')}>
            <ul className="pricing-column-features">
              <li>
                <i className={cx(style.warningIcon, 'fa fa-info-circle')}></i>
                <span>
                  {' '}
                  <strong>Personalized packaged</strong> for more than <strong>100</strong> students
                </span>
              </li>
            </ul>
          </Row>
        </Col>
        <Col xs={12} md={4} className="pricing-column">
          <div className={cx(style.colBorderTop, 'pricing-column-body')}>
            <Formik
              initialValues={initialValues}
              onSubmit={handleOnSubmit}
              validationSchema={validationSchema}>
              {({ values, isSubmitting, handleSubmit }) => {
                const requirePersonalizedPackage =
                  values.orderItems.reduce((total, item) => {
                    return total + item.purchasableCount;
                  }, 0) >= 100;
                return (
                  <Form id="subscription_payment_fields" className={style.formikForm}>
                    <Field name="authenticityToken" type="hidden" />

                    <MultiConfirmationFormGroup
                      values={values}
                      requirePersonalizedPackage={requirePersonalizedPackage}
                      variant="pricing"
                      className={style.multipleSubscriptionsForm}
                    />

                    <div className="form-group">
                      {requirePersonalizedPackage ? (
                        <PrimaryButton
                          onClick={() => {
                            window.open('mailto:schools@magoosh.com', '_blank');
                          }}
                          className={style.submitButton}>
                          Contact Us
                        </PrimaryButton>
                      ) : (
                        <PrimaryButton
                          onClick={handleSubmit}
                          disabled={isSubmitting}
                          className={style.submitButton}>
                          Order now for{' '}
                          {formatPrice(
                            calculateSum(
                              values.orderItems.map((item) =>
                                calculateSubtotal(item.purchasable.priceCents, item.purchasableCount)
                              )
                            ),
                            values.orderItems[0].purchasable.currency
                          )}
                        </PrimaryButton>
                      )}
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </Col>
      </Row>

      <div className={'bulk-faq'}>
        <h1 className={'text-center'}>Frequently Asked Questions</h1>
        <Row>
          <Col md={6}>
            <h4 className="text-primary font-weight-bold">What do I get when I purchase Magoosh?</h4>
            <p>
              Once you complete your purchase, you’ll get access to an instructor dashboard where you can
              distribute student accounts, view student activity, and access the student view. You’ll also
              have access to our team of partnership specialists who are available to answer your questions
              and ensure you have what you need to support students. Both you and your students will have 12
              months of access to the full Magoosh product.
            </p>
          </Col>
          <Col md={6}>
            <h4 className="text-primary font-weight-bold">How do I get started?</h4>
            <p>
              After purchasing Magoosh, you’ll be taken to a page where you can let us know how many
              instructor accounts you need, when you’d like to get started, and if you have any additional
              notes or questions for our team. Once your accounts are set up (usually within 24 hours), you’ll
              get an email from our team that will help you get acquainted with your Magoosh dashboard and
              distributing student accounts!
            </p>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <h4 className="text-primary font-weight-bold">
              Can I purchase different lengths of subscriptions?
            </h4>
            <p>
              We’ve found that when working with large groups of students, it’s helpful to err on the side of
              more time rather than less, since some students will need more time than others. Our 10+ pricing
              is the most affordable option we provide to partners, and also comes with the most time for
              students and instructors! If you’d like to purchase a shorter subscription, our individual
              subscriptions will be your best option!
            </p>
          </Col>
          <Col md={6}>
            <h4 className="text-primary font-weight-bold">
              What if I have fewer than 10 students or more than 100?
            </h4>
            <p>
              If you have fewer than 10 students, our individual subscriptions will be your best option, as we
              unfortunately can’t support smaller partner purchases at this time.
            </p>
            <p>
              If you have 100 students or more, please reach out to our team at{' '}
              <a href="mailto:schools@magoosh.com">schools@magoosh.com</a>, or fill out our partnership form
              here:{' '}
              <a href="https://schools.magoosh.com/high-school-partnership-request-form" target="_blank">
                High School Inquiry
              </a>
              .
            </p>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default PurchaseOptions;
